import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import logo from "../../../Assets/Images/logo.png";
import axios from "axios";
import { MAIN_URL } from "../../../Configurations/Urls";
import { Eye, EyeOff } from "lucide-react";
import {
  fetchGeneralCountries,
  fetchGeneralCountriesall,
} from "../../../Apis/OrganizationLocation";
import SelectWithImage from "../../../Components/Inputs/SelectWithImage";
import {
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import {
  SIGNUP_AUTOCOMPLETE,
  signupSensitiveInputProps,
} from "../../../utils/signupFormAutofill";

const SpinSVG = () => (
  <svg width="18" height="18" viewBox="0 0 15 15" fill="none"
    style={{ animation: "spin 0.8s linear infinite", flexShrink: 0 }}>
    <circle cx="7.5" cy="7.5" r="5.5" stroke="currentColor" strokeOpacity=".3" strokeWidth="2.5" fill="none" />
    <path d="M7.5 2A5.5 5.5 0 0 1 13 7.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" />
  </svg>
);

const FreeTrialPage = ({
  title = "Start Free Trial",
  appName = "HR ERP",
  description,
}) => {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState(null);
  const [agree, setAgree] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [strength, setStrength] = useState("");
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [formErrors, setFormErrors] = useState("");

  const [showConflictModal, setShowConflictModal] = useState({
    show: false,
    title: "",
    message: "",
    actionType: "", // can be 'email' or 'phone'
  }); // ⬅️ NEW STATE

  const navigate = useNavigate();

  useEffect(() => {
    setEmail("");
    setPassword("");
  }, []);

  useEffect(() => {
    fetchGeneralCountriesall()
      .then((data) => {
        const formatted = data.countries.map((item) => ({
          name: item.country_name,
          code: item.country_code,
          phone_code: item.country_phone_code,
        }));
        setCountries(formatted);
        setCountryCode(formatted.find((item) => item.code === "IN"));
      })
      .catch((err) => setFormErrors(err.message));
  }, []);

  useEffect(() => {
    document.body.style.overflow = showConflictModal ? "hidden" : "auto";
  }, [showConflictModal]);

  const validateForm = () => {
    const formErrors = {};
    let isValid = true;

    if (!fullName.trim()) {
      formErrors.fullName = "Full Name is required";
      isValid = false;
    }

    if (!email) {
      formErrors.email = "Email is required";
      isValid = false;
    } else if (
      !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email)
    ) {
      formErrors.email = "Invalid email format";
      isValid = false;
    }

    if (!password) {
      formErrors.password = "Password is required";
      isValid = false;
    } else if (password?.length < 8) {
      formErrors.password = "Password must be at least 8 characters";
      isValid = false;
    }

    if (!phone) {
      formErrors.phone = "Phone number is required";
      isValid = false;
    } else {
      const parsed = parsePhoneNumberFromString(phone, countryCode?.code);
      if (!parsed || !parsed.isValid()) {
        formErrors.phone = "Enter a valid phone number";
        isValid = false;
      } else {
        setPhone(parsed.nationalNumber);
      }
    }

    if (!agree) {
      formErrors.agree = "You must agree to the Terms of Service";
      isValid = false;
    }

    setErrors(formErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const payload = {
        email: email,
        full_name: fullName,
        hash_password: password,
        phone: phone,
        country: `${countryCode.name}`,
        phone_code: `${countryCode.phone_code}`,
      };

      let res = await axios.post(`${MAIN_URL}/api/auth/create-user`, {
        email,
        phone,
      });
      console.log("res is ", res);

      navigate("/otp-verify", { state: { ...payload } });
    } catch (error) {
      if (error.response && error.response.data) {
        // const { errors, message, status, data } = error.response;

        const { errors, message } = error.response.data; // ✅ FIXED
        const status = error.response.status; // ✅ FIXED

        if (status === 409 && message === "Phone number already exists.") {
          setShowConflictModal({
            show: true,
            title: "Phone Number Already Registered",
            message:
              "This phone number is already registered. Would you like to log in instead or continue registration?",
            actionType: "phone",
          });
          setErrors((prev) => ({
            ...prev,
            phone: "Phone number already in use",
          }));
          toast.error("Phone number already registered.");
          return;
        }

        if (status === 409 && message === "User already Exist.") {
          setShowConflictModal({
            show: true,
            title: "Email Already Registered",
            message:
              "This email is already registered. Would you like to log in instead or continue registration?",
            actionType: "email",
          });
          setErrors((prev) => ({ ...prev, email: "Email already in use" }));
        } else if (errors) {
          setErrors(errors);
        } else {
          toast.error(message || "Registration failed.");
        }
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ems-auth-root">

      <div className="ems-auth-left">
        <div className="ems-auth-card">

          <div className="ems-auth-brand">
            <img src={logo} alt="HR ERP" className="ems-auth-brand-logo" />
          </div>

          <h1 className="ems-auth-h">{title}</h1>
          <p className="ems-auth-sub">Create your account and get started today.</p>

          <form
            onSubmit={handleSubmit}
            noValidate
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
              }
            }}
            {...SIGNUP_AUTOCOMPLETE.form}
          >
            <div className="ems-auth-field">
              <label htmlFor="trial-name">Full Name</label>
              <input
                id="trial-name"
                placeholder="John Doe"
                type="text"
                value={fullName}
                onChange={(e) => { setFullName(e.target.value); setErrors((p) => ({ ...p, fullName: "" })); }}
                className="ems-fm-input"
                {...SIGNUP_AUTOCOMPLETE.fullName}
              />
              {errors.fullName && <span className="ems-fm-err">{errors.fullName}</span>}
            </div>

            <div className="ems-auth-field">
              <label htmlFor="trial-email">Email Address</label>
              <input
                id="trial-email"
                placeholder="name@company.com"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: "" })); }}
                className="ems-fm-input"
                {...SIGNUP_AUTOCOMPLETE.email}
                {...signupSensitiveInputProps}
              />
              {errors.email && <span className="ems-fm-err">{errors.email}</span>}
            </div>

            <div className="ems-auth-field">
              <label htmlFor="trial-pw">Password</label>
              <div className="ems-auth-pw">
                <input
                  id="trial-pw"
                  placeholder="Create a secure password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPassword(value);
                    const hasUpperCase = /[A-Z]/.test(value);
                    const hasNumber = /\d/.test(value);
                    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(
                      value
                    );
                    const isLongEnough = value.length >= 8;
                    let error = "";
                    if (!isLongEnough)
                      error =
                        "Password must be at least 8 characters long.";
                    else if (!hasUpperCase)
                      error = "Include at least one uppercase letter.";
                    else if (!hasNumber)
                      error = "Include at least one number.";
                    else if (!hasSpecialChar)
                      error = "Include at least one special character.";
                    setErrors((prev) => ({ ...prev, password: error }));
                    // Strength detection
                    const strengthCount = [
                      hasUpperCase,
                      hasNumber,
                      hasSpecialChar,
                      isLongEnough,
                    ].filter(Boolean).length;
                    if (strengthCount <= 2) setStrength("Weak");
                    else if (strengthCount === 3) setStrength("Medium");
                    else setStrength("Strong");
                  }}
                  className="ems-fm-input"
                  {...SIGNUP_AUTOCOMPLETE.password}
                  {...signupSensitiveInputProps}
                />
                <button
                  type="button"
                  className="ems-auth-pw-eye"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <span className="ems-fm-err">{errors.password}</span>}

              {password && (
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    marginTop: 4,
                    color:
                      strength === "Weak"
                        ? "#ef4444"
                        : strength === "Medium"
                          ? "#eab308"
                          : "#22c55e",
                  }}
                >
                  Password Strength: {strength}
                </div>
              )}

              {password && (
                <div style={{ height: 4, background: "var(--ems-border)", borderRadius: 4, marginTop: 4 }}>
                  <div
                    style={{
                      height: 4,
                      borderRadius: 4,
                      transition: "all .3s",
                      width:
                        strength === "Weak"
                          ? "33%"
                          : strength === "Medium"
                            ? "66%"
                            : "100%",
                      background:
                        strength === "Weak"
                          ? "#ef4444"
                          : strength === "Medium"
                            ? "#eab308"
                            : "#22c55e",
                    }}
                  />
                </div>
              )}

              {!errors.password && (
                <span style={{ fontSize: 12, color: "var(--ems-faint)", fontWeight: 500 }}>
                  Minimum 8 characters, with at least one uppercase letter, number, and symbol.
                </span>
              )}
            </div>

            <div className="ems-auth-field">
              <label>Phone Number</label>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <SelectWithImage
                  value={countryCode}
                  error={false}
                  options={countries}
                  optionkey={"phone_code"}
                  onChange={(e, val) => setCountryCode(val)}
                  width={"60%"}
                  minWidth="20px"
                  disabled={false}
                  searchKeys={["name", "phone_code"]}
                />
                <input
                  type="tel"
                  placeholder="Enter Phone Number"
                  value={phone}
                  style={{ height: 42 }}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\d{0,15}$/.test(val)) {
                      setPhone(val);
                      setErrors((p) => ({ ...p, phone: "" }));
                    }
                  }}
                  className="ems-fm-input"
                  {...SIGNUP_AUTOCOMPLETE.phone}
                />
              </div>
              {errors.phone
                ? <span className="ems-fm-err">{errors.phone}</span>
                : <span style={{ fontSize: 12, color: "var(--ems-faint)", fontWeight: 500 }}>
                    Number used for alerts or account recovery
                  </span>
              }
            </div>

            <div className="ems-auth-meta" style={{ marginBottom: 24 }}>
              <label className="ems-auth-remember">
                <input
                  type="checkbox"
                  checked={agree}
                  onChange={() => { setAgree(!agree); setErrors((p) => ({ ...p, agree: "" })); }}
                  id="termsCheck"
                />
                I agree to the{" "}
                <Link to="/terms" style={{ color: "var(--ems-primary)", fontWeight: 650 }}>Terms of Service</Link>
                {" "}and{" "}
                <Link to="/privacy" style={{ color: "var(--ems-primary)", fontWeight: 650 }}>Privacy Policy</Link>.
              </label>
            </div>
            {errors.agree && (
              <span className="ems-fm-err" style={{ display: "block", marginBottom: 12, marginTop: -16 }}>
                {errors.agree}
              </span>
            )}

            <div className="ems-auth-actions">
              <button type="submit" className="ems-btn ems-primary" disabled={loading}>
                {loading ? <><SpinSVG /> Creating account…</> : "Continue"}
              </button>
            </div>

            <p style={{ textAlign: "center", marginTop: 20, fontSize: 13, color: "var(--ems-muted)", fontWeight: 500 }}>
              Already have an account?{" "}
              <Link to="/login" style={{ color: "var(--ems-primary)", fontWeight: 650, textDecoration: "none" }}>
                Login
              </Link>
            </p>
          </form>

        </div>
      </div>

      <div className="ems-auth-right">
        <div className="ems-auth-hero">
          <div className="ems-auth-hero-mark">
            <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
              <path d="M5 8h16M5 13h10M5 18h16" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </div>
          <h2 className="ems-auth-hero-h">{appName}</h2>
          <p className="ems-auth-hero-p">
            {description ||
              "Start your free trial and experience the full power of our platform today."}
          </p>
          <div className="ems-auth-trust">
            <span className="ems-auth-trust-dot" />
            Join thousands of happy users.
          </div>
        </div>
        <p className="ems-auth-copy">&copy; {new Date().getFullYear()} {appName}. All rights reserved.</p>
      </div>

      {showConflictModal.show && (
        <div className="ems-org-overlay" role="dialog">
          <div className="ems-org-picker" style={{ maxWidth: 480 }}>
            <div className="ems-org-picker-hd">
              <h3 className="ems-org-picker-h">{showConflictModal.title}</h3>
              <p className="ems-org-picker-sub">{showConflictModal.message}</p>
            </div>
            <div className="ems-org-foot">
              <button
                type="button"
                className="ems-btn ems-ghost"
                onClick={() => {
                  setShowConflictModal({ ...showConflictModal, show: false });
                  navigate("/login");
                }}
              >
                Login
              </button>
              <button
                type="button"
                className="ems-btn ems-primary"
                onClick={() => {
                  setShowConflictModal({ ...showConflictModal, show: false });
                  if (showConflictModal.actionType === "email") {
                    navigate("/email-register", { state: { email } });
                  } else if (showConflictModal.actionType === "phone") {
                    navigate("/email-register", { state: { phone } });
                  }
                }}
              >
                Continue Registration
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FreeTrialPage;
